import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useState } from 'react';
import { DriveSweepModel } from '../../../../../types';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../../state/zustand-store';
import { UserKeys, updateDriveMonitor } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import GeneralDialog from '../../../../shared/general-dialog';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { ModuleList } from '../../../../shared/analytics/module-list';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface EditDriveSweepDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
  selectedDriveSweep: DriveSweepModel;
}

export default function EditDriveSweepDialog({
  open,
  handleClose,
  changeLogRoles,
  selectedDriveSweep,
}: EditDriveSweepDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
  } = useForm<Partial<DriveSweepModel>>({
    values: {
      ...selectedDriveSweep,
    },
  });

  const onSubmit: SubmitHandler<Partial<DriveSweepModel>> = (data) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanEditDriveSweep.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const newSweep: Partial<DriveSweepModel> = {
        ...data,
      };
      mutate(newSweep);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<DriveSweepModel>) =>
      updateDriveMonitor(data, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Drive Sweep edited successfully!',
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveSweepModels],
      });

      setChangeLogMessage('');
      handleClose();
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue editing this drive sweep. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.drive.drive_sweep.edit_drive_sweep, {
        module: ModuleList.modules.drive.name,
        section: ModuleList.modules.drive.sections.driveSweep,
      });
      setOpenChangeLogDialog(false);
    },
  });

  return (
    <GeneralDialog
      open={open}
      maxWidth="md"
      title="Edit Drive Sweep"
      handleClose={() => {
        handleClose();
        reset();
      }}
      dialogContent={
        <Stack px={2} py={1}>
          <Typography sx={{ mb: 2, fontWeight: 500 }}>
            You can update the name and description of this drive sweep. If
            you'd like to change the folder, you'll need to create a new drive
            sweep.
          </Typography>
          <Stack flexDirection="row" gap={1}>
            <Controller
              name="name"
              control={control}
              rules={{
                required: 'Name is required.',
              }}
              render={({ field }) => (
                <TextField
                  required
                  autoComplete="off"
                  inputProps={{
                    id: 'name',
                  }}
                  InputLabelProps={{
                    htmlFor: 'name',
                  }}
                  error={!!errors.name}
                  helperText={errors?.name?.message || ' '}
                  label="Name"
                  sx={{ width: 400 }}
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="desc"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'desc',
                  }}
                  InputLabelProps={{
                    htmlFor: 'desc',
                  }}
                  label="Description"
                  sx={{ width: 400 }}
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </Stack>

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const newTemplate: Partial<DriveSweepModel> = {
                ...getValues(),
              };
              mutate(newTemplate);
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Save',
        action: handleSubmit(onSubmit),
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
